import Bus from '../../bus'
import ApiManager from '../../api/request'

import { isEmpty } from 'lodash'
import {
  logger,
  getUID,
  mergeUpdate
} from '../../utils'

import BROADCASTER_EVENTS_MAP from './events.map'

class WSServiceError extends Error {
  constructor (message) {
    super(message)
    this.name = 'WSServiceError'
  }
}

/*
  Handling socket client connection
*/

const SOCKET_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
}

class WebSocketManager {
  constructor () {
    this.socket = null
    this.connectionAttempts = 0
    this.reconnectionEnabled = true
    this.reconnectionTimeoutId = null
    this.buffer = []
    this.connected = false
    this.connectionId = getUID(8)
    this.connectionTime = Date.now()
    this.options = {}

    // Enable buffering
    Bus.on(Bus.events.socket.subscribe, this.handleBufferSubscribeActions)
    Bus.on(Bus.events.socket.unsubscribe, this.handleBufferUnsubscribeActions)
  }

  connect = (options = {}) => {
    try {
      if (this.socket && (this.socket.readyState !== SOCKET_STATE.CLOSED)) {
        logger.debug('[socket manager]', 'connection not closed, aborting')
        return
      }

      this.options = mergeUpdate(this.options, options)

      logger.debug('[socket manager] connecting', this.options)

      // Inc connection attempts counter
      this.connectionAttempts++
      // Cancel reconnection timeout
      window.clearTimeout(this.reconnectionTimeoutId)

      this.initializeWSClient()
      this.initializeHandlers()

      // this.socket.connect()
    } catch (error) {
      logger.debug('[socket manager]', 'unable to connect', error.message)
    }
  }

  disconnect = () => {
    if (this.socket && (this.socket.readyState === SOCKET_STATE.OPEN)) {
      logger.debug('[socket manager]', 'disconnecting application')

      // Disabling subscription handlers
      Bus.off(Bus.events.socket.subscribe, this.handleSocketSubscribeActions)
      Bus.off(Bus.events.socket.unsubscribe, this.handleSocketUnsubscribeActions)

      // Closing connection
      this.socket.close()
      // this.socket = null
    }
  }

  initializeWSClient = () => {
    const connectionURL = new URL(ApiManager.getHostname('ws'))

    if (!connectionURL) {
      throw new Error('WSClient URL is broken')
    }

    // Applying connection options to connection URL
    if (!isEmpty(this.options)) {
      const connectionParams = new URLSearchParams()

      if (this.options.projectId) {
        connectionParams.set('pid', this.options.projectId)
      }

      connectionURL.search = connectionParams
    }

    this.socket = new WebSocket(connectionURL.href)
    logger.debug('[socket manager] client initialized', `socket.readyState=${this.socket.readyState}`)
  }

  initializeHandlers = () => {
    const socket = this.socket

    if (!socket) {
      throw new Error('WSClient is not initialized')
    }

    socket.addEventListener('open', (options) => {
      logger.debug('[socket manager]', '✔️', 'connection opened')

      // Disable buffering
      Bus.off(Bus.events.socket.subscribe, this.handleBufferSubscribeActions)
      Bus.off(Bus.events.socket.unsubscribe, this.handleBufferUnsubscribeActions)

      // Enable subscriptioning
      Bus.on(Bus.events.socket.subscribe, this.handleSocketSubscribeActions)
      Bus.on(Bus.events.socket.unsubscribe, this.handleSocketUnsubscribeActions)

      const newConnectionTime = Date.now()
      const diffConnectionTime = (newConnectionTime - this.connectionTime)
      this.connectionTime = newConnectionTime
      this.connected = true

      /*
        For too long connections opening we recreating connection id
        Need for triggering & reinitializing ui data
      */
      if (diffConnectionTime > 2000) {
        /*
          Creating new connection id for refetching data
        */
        logger.debug('[socket manager]', 'creating new connection id')
        this.connectionId = getUID(8)
      }

      /*
        Once connection established - applying subscriptions buffer
        for recovering early calls
      */
      if (this.buffer.length > 0) {
        logger.debug('[socket manager]', `applying buffer [n=${this.buffer.length}]`)
        for (const bufferedMessage of this.buffer) {
          socket.send(bufferedMessage)
        }
        this.buffer = []
      }

      /*
        Setting connection attepts to zero
        so it will remove delay from reconnection timeout
      */
      this.connectionAttempts = 0

      Bus.send({
        event: Bus.events.socket.open,
        data: {
          connectionId: this.connectionId
        }
      })
    })

    socket.addEventListener('message', event => {
      const data = JSON.parse(event.data)

      // logger.debug('[socket manager]', '🗃️', 'data', data)

      const eventAction = this.prepareEventFromSocketMessage(data)

      Bus.send({
        event: Bus.events.socket.event,
        data: eventAction
      })
    })

    socket.addEventListener('error', error => {
      logger.debug('[socket manager]', '☠️', 'socket error', error)
    })

    socket.addEventListener('close', () => {
      logger.debug('[socket manager]', '❌', 'connection closed')

      this.connected = false
      // Disabling subscription handlers
      Bus.off(Bus.events.socket.subscribe, this.handleSocketSubscribeActions)
      Bus.off(Bus.events.socket.unsubscribe, this.handleSocketUnsubscribeActions)

      if (this.reconnectionEnabled && !this.reconnectionTimeoutId) {
        const reconnectionTimeout = this.getNextReconnectionTimeout()

        logger.debug(
          '[socket manager]',
          '⌛',
          `reconnection attempt #${this.connectionAttempts} in ${reconnectionTimeout}ms`
        )

        // Creating reconnection handler
        const reconnectionHandler = () => {
          if (this.socket && (this.socket.readyState !== SOCKET_STATE.CLOSED)) {
            logger.debug('[socket manager]', 'already reconnected')
            return
          }

          logger.debug('[socket manager]', 'reconnecting')

          // Inc connection attempts counter
          this.connectionAttempts++

          try {
            // Reinitializing new socket connection
            this.initializeWSClient()
            /*
              Enabling buffering so resubscribtion could be done any time
            */
            Bus.on(Bus.events.socket.subscribe, this.handleBufferSubscribeActions)
            Bus.on(Bus.events.socket.unsubscribe, this.handleBufferUnsubscribeActions)

            // Adding handlers for new socket connection
            this.initializeHandlers()

            /*
              Broadcasting resubscribe event
              SubscriptionManager will handle and send his subscriptionKeys to buffer
            */
            Bus.emit(Bus.events.socket.resubscribe)
          } catch (error) {
            logger.debug('[socket manager]', 'unable to reconnect', error.message)
          }

          this.reconnectionTimeoutId = null
        }

        // Applying in timeout
        this.reconnectionTimeoutId = window.setTimeout(
          reconnectionHandler,
          reconnectionTimeout
        )
      }
      /*
        TODO: On reconnection, reset the transports option, as the Websocket
        connection may have failed (caused by proxy, firewall, browser, ...)
        Bus.send({
          event: 'reconnecting'
        })
        or
        this.Bus.send({
          event: 'reconnect_failed'
        })
        socket.options.transports = ['polling', 'websocket']
      */

      Bus.send({ event: Bus.events.socket.closed })
    })
  }

  getConnectionDuration () {
    return Date.now() - this.connectionTime
  }

  getNextReconnectionTimeout = () => {
    return Math.round(
      (Math.log(this.connectionAttempts + 1) + 1) * 1000
    )
  }

  handleSocketSubscribeActions = payload => {
    const {
      projectId
    } = this.options

    const message = JSON.stringify({
      messageType: 'SUBSCRIBE',
      // FIXME: Temp sportsbook fix
      payload: {
        ...payload,
        projectId
      }
    })

    this.socket.send(message)
  }

  handleSocketUnsubscribeActions = payload => {
    const {
      projectId
    } = this.options

    const message = JSON.stringify({
      messageType: 'UNSUBSCRIBE',
      payload: {
        ...payload,
        projectId
      }
    })

    this.socket.send(message)
  }

  handleBufferSubscribeActions = payload => {
    this.buffer.push(
      JSON.stringify({ messageType: 'SUBSCRIBE', payload })
    )
  }

  handleBufferUnsubscribeActions = payload => {
    this.buffer.push(
      JSON.stringify({ messageType: 'UNSUBSCRIBE', payload })
    )
  }

  prepareEventFromSocketMessage (data) {
    const {
      notificationTopic,
      payload
    } = data

    if (!notificationTopic) {
      throw new WSServiceError('notificationTopic not found')
    }

    const actionType = this.getActionTypeFromNotificationTopic(notificationTopic)

    if (!actionType) {
      throw new WSServiceError(`Notification topic [${notificationTopic}] not mapped`)
    }

    /*
      Returning action
     */
    return {
      type: actionType,
      payload
    }
  }

  /*
    Maps subscription data from sockets to the application event
   */
  getActionTypeFromNotificationTopic (notificationTopic) {
    const internalEventName = BROADCASTER_EVENTS_MAP[notificationTopic]

    if (!internalEventName) {
      return null
    }

    return internalEventName
  }
}

const WSManager = new WebSocketManager()

if (process.env.NODE_ENV === 'development') {
  window.WSManager = WSManager
}

export default WSManager
